import { useInfiniteQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type {
  ConversationPlatform,
  ConversationSearchParams,
  ConversationSearchResponse,
  ConversationSearchSortBy,
  ConversationSearchRole,
} from './types';

export async function searchConversations(params: ConversationSearchParams): Promise<ConversationSearchResponse> {
  const { artistId, q, cursor, limit = 20, sortBy, role, dateStart, dateEnd, platform } = params;

  if (!artistId) throw Error('Artist ID is required');
  if (!q) throw Error('Search query is required');

  const queryParams: Record<string, string | number> = {
    q,
    limit,
  };

  if (cursor) {
    queryParams.cursor = cursor;
  }

  if (sortBy) {
    queryParams.sort_by = sortBy;
  }

  if (role && role !== 'all') {
    queryParams.role = role;
  }

  if (dateStart) {
    queryParams.date_start = dateStart;
  }

  if (dateEnd) {
    queryParams.date_end = dateEnd;
  }

  if (platform && platform.length > 0) {
    queryParams.platform = platform.join(',');
  }

  const url = `/web/studio/artists/${artistId}/conversations/search`;
  const response = await api.executeGetRequest<{ data: ConversationSearchResponse }>(url, queryParams);

  return response.data.data;
}

export type UseConversationSearchOptions = {
  limit?: number;
  sortBy?: ConversationSearchSortBy;
  role?: ConversationSearchRole;
  dateStart?: string;
  dateEnd?: string;
  platform?: ConversationPlatform[];
};

export function useConversationSearch(artistId: string, query: string, options?: UseConversationSearchOptions) {
  const user = storage.getUser();
  const { limit = 20, sortBy, role, dateStart, dateEnd, platform } = options ?? {};

  return useInfiniteQuery({
    initialPageParam: undefined as string | undefined,
    enabled: !!artistId && !!user?.is_registered && query.length >= 1,
    queryKey: queryKeys.artistConversationSearch(artistId, {
      query,
      limit,
      sortBy,
      role,
      dateStart,
      dateEnd,
      platform,
    }),
    queryFn: ({ pageParam }) =>
      searchConversations({
        artistId,
        q: query,
        cursor: pageParam,
        limit,
        sortBy,
        role,
        dateStart,
        dateEnd,
        platform,
      }),
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
  });
}
